import { DateFormat } from '@heidi/schema/ml-scribe';
import { convertMomentToDateFnsFormat } from '@heidi/utils/dates';

import {
  addDays,
  differenceInCalendarDays,
  endOfDay,
  format,
  startOfDay,
} from 'date-fns';
import type { IntlShape } from 'react-intl';
import { getSessionDataConfig } from '../config';
import { isStartNowConsumedSession } from './start-now-consumed-session';

/**
 * Date buckets for the session list's grouped headers, ordered newest-first to
 * match the list's `created_time_local` descending sort. Calendar grouping
 * (`getSessionDateGroup`) puts only dates ahead of today in `upcoming`.
 * Presentation grouping (`getSessionListDateGroup`) also puts uncommenced
 * later-today sessions in `upcoming`.
 */
export type SessionDateGroup = 'upcoming' | 'today' | 'yesterday' | 'older';

export type SessionDateFields = {
  created_at?: string | null;
  created_time_local?: string | null;
  ehr_appt_id?: string | null;
  session_id?: string | null;
  session_status?: string | null;
};

/**
 * A session's creation instant as a local `Date`, via the single canonical
 * converter (`parseTimeStampOrNull`, which shares `parseTimeStamp`'s exact
 * parse + zone-detection so the two can never diverge). App-created session
 * times are UTC and get converted to the user's timezone; EHR appointment times
 * are naive local wall-clock and are read as-is — keyed off `ehr_appt_id`,
 * mirroring `useFormatSessionTime(!isSessionFromEhrAppt)` used across the app.
 * Reading a UTC time as local dropped a calendar day for users ahead of UTC,
 * landing a just-created session in the wrong group. The local field remains
 * preferred, but a malformed value falls back to a valid server timestamp.
 * Returns null when neither field is usable.
 */
export function getSessionLocalDate(session: SessionDateFields): Date | null {
  const parseTimeStampOrNull = getSessionDataConfig().parseTimeStampOrNull;
  const isUtc = !session.ehr_appt_id;
  for (const candidate of [session.created_time_local, session.created_at]) {
    if (typeof candidate === 'string' && candidate.length > 0) {
      const date = parseTimeStampOrNull(candidate, isUtc);
      if (date) return date;
    }
  }
  return null;
}

/**
 * Buckets a session's local date relative to `now`. Pure: `now` is injected so
 * the calendar-day boundaries are deterministic and testable across timezones.
 */
export function getSessionDateGroup(date: Date, now: Date): SessionDateGroup {
  const dayDelta = differenceInCalendarDays(date, now);
  if (dayDelta > 0) return 'upcoming';
  if (dayDelta === 0) return 'today';
  if (dayDelta === -1) return 'yesterday';
  return 'older';
}

/**
 * Session-list presentation grouping. A same-day appointment still ahead of
 * the list's stable clock belongs to Upcoming until Start is spent: recording
 * is active, stop is in flight (`stoppedSessionIds`), or status is past EMPTY
 * ({@link isStartNowConsumedSession}). That state wins only for this same-day
 * exception; established calendar groups keep their date meaning.
 */
export function getSessionListDateGroup(
  session: SessionDateFields,
  now: Date,
  activeSessionId?: string | null,
  stoppedSessionIds?: ReadonlySet<string> | null
): SessionDateGroup {
  const date = getSessionLocalDate(session);
  if (!date) return 'older';

  const calendarGroup = getSessionDateGroup(date, now);
  if (calendarGroup !== 'today' || date.getTime() <= now.getTime()) {
    return calendarGroup;
  }

  const hasCommenced =
    (session.session_id != null && session.session_id === activeSessionId) ||
    isStartNowConsumedSession(session, stoppedSessionIds);
  return hasCommenced ? 'today' : 'upcoming';
}

/**
 * Makes each date bucket contiguous before the run-based DataTable sectioning
 * is applied. Valid rows within a bucket retain their upstream order. Missing
 * or malformed dates are the chronological minimum: after valid Older rows in
 * descending order, before them in ascending order.
 */
export function orderSessionsByDateGroup<T extends SessionDateFields>(
  sessions: readonly T[],
  now: Date,
  groupOrder: readonly SessionDateGroup[],
  getGroup?: (session: T) => SessionDateGroup
): T[] {
  const groupRank = new Map(groupOrder.map((group, index) => [group, index]));
  const invalidDatesFirst = groupOrder[0] === 'older';

  return sessions
    .map((session, originalIndex) => {
      const date = getSessionLocalDate(session);
      return {
        session,
        originalIndex,
        group: getGroup?.(session) ?? (date ? getSessionDateGroup(date, now) : 'older'),
        hasValidDate: date !== null,
      };
    })
    .toSorted((a, b) => {
      const groupDiff = (groupRank.get(a.group) ?? 0) - (groupRank.get(b.group) ?? 0);
      if (groupDiff !== 0) return groupDiff;

      if (a.hasValidDate !== b.hasValidDate) {
        if (invalidDatesFirst) return a.hasValidDate ? 1 : -1;
        return a.hasValidDate ? -1 : 1;
      }

      return a.originalIndex - b.originalIndex;
    })
    .map(({ session }) => session);
}

/**
 * Inclusive date bounds for a group's own backend query (each group is a
 * separate date-bounded request to the same endpoint). Open-ended on the side
 * that runs to infinity: `upcoming` has no upper bound, `older` no lower bound.
 * Relies on the endpoint accepting a one-sided range.
 */
export function getSessionDateGroupRange(
  group: SessionDateGroup,
  now: Date
): { from?: Date; to?: Date } {
  switch (group) {
    case 'upcoming':
      return { from: startOfDay(addDays(now, 1)) };
    case 'today':
      return { from: startOfDay(now), to: endOfDay(now) };
    case 'yesterday':
      return { from: startOfDay(addDays(now, -1)), to: endOfDay(addDays(now, -1)) };
    case 'older':
      return { to: endOfDay(addDays(now, -2)) };
  }
}

/**
 * True for the only RTL app locale (Arabic). Arabic session-list dates/times
 * must go through the `Intl` locale formatter so the month name localizes and
 * the rendered string is RTL-native — a bare `date-fns` English string like
 * "3 Jul 2026" bidi-scrambles to "Jul 2026 3" inside the RTL cell (LOC-503).
 *
 * The locale *detection* matches `useFormatSessionTime`'s `startsWith('ar')`
 * check, but the digit handling intentionally diverges: that hook forces Latin
 * digits (`numberingSystem: 'latn'`), whereas these formatters keep `Intl`'s
 * native Arabic-Indic digits — the "٣ يوليو ٢٠٢٦" form LOC-503 asks for. So the
 * Date/Time columns and the (hidden-by-default) Last Updated column render
 * different numeral systems by design.
 */
const isArabicLocale = (intl: IntlShape): boolean =>
  intl.locale?.toLowerCase().startsWith('ar') ?? false;

const isEnglishLocale = (intl: IntlShape): boolean =>
  intl.locale?.toLowerCase().startsWith('en') ?? false;

const formatLocalizedSessionListDate = (
  date: Date,
  intl: IntlShape,
  options: Intl.DateTimeFormatOptions
): string => {
  if (isArabicLocale(intl)) {
    return intl.formatDate(date, { ...options, calendar: 'gregory' });
  }
  return intl.formatDate(date, options);
};

/**
 * Formats a session's local date for the list's Date column. English keeps the
 * existing `date-fns` "3 Jul 2026" output; Arabic and all other app locales
 * route through `intl.formatDate` so month names localize (French "15 mai 2026",
 * Arabic Gregorian "٣ يوليو ٢٠٢٦", etc.).
 */
export function formatSessionListDate(date: Date, intl: IntlShape): string {
  if (isEnglishLocale(intl)) {
    return format(date, convertMomentToDateFnsFormat(DateFormat.DAY_MONTH_YEAR_ABBR));
  }
  return formatLocalizedSessionListDate(date, intl, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Expanded Start Now date column. Includes the weekday for quick scanning while
 * retaining the full day, month, and year from the fullscreen date treatment.
 */
export function formatSessionListDateWithWeekday(date: Date, intl: IntlShape): string {
  if (isEnglishLocale(intl)) {
    return format(date, 'EEE, d MMM yyyy');
  }
  return formatLocalizedSessionListDate(date, intl, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Year-less variant of {@link formatSessionListDate} ("3 Jul") for the SCR-6071
 * split-view date subtitle, where the year adds little at a glance and the full
 * date remains available in the fullscreen Date column.
 */
export function formatSessionListDateShort(date: Date, intl: IntlShape): string {
  if (isEnglishLocale(intl)) {
    return format(date, 'd MMM');
  }
  return formatLocalizedSessionListDate(date, intl, {
    day: 'numeric',
    month: 'short',
  });
}

/**
 * Date-group shapes the stacked Start Now sub-label can render. Progressive
 * selection picks `withoutYear` / `withoutDate` / `full`; `now` marks the
 * Start/Stop action-row session (`session_id` equals `startNowSessionId`).
 */
export type StackedDateVariant = 'full' | 'withoutYear' | 'withoutDate' | 'now';

/**
 * Chooses the stacked date-group shape for a session row relative to `now`.
 * Today/yesterday omit the date; within seven calendar days (past or future)
 * show weekday + day-month; beyond that add the year. If `startNowSessionId`
 * matches `session.session_id`, returns `now` and skips those calendar rules.
 */
export function resolveStackedDateVariant(
  session: SessionDateFields,
  now: Date,
  startNowSessionId?: string | null
): StackedDateVariant {
  if (startNowSessionId && session.session_id === startNowSessionId) return 'now';
  const date = getSessionLocalDate(session);
  if (!date) return 'full';
  const dayDelta = differenceInCalendarDays(date, now);
  if (dayDelta === 0 || dayDelta === -1) return 'withoutDate';
  if (Math.abs(dayDelta) <= 7) return 'withoutYear';
  return 'full';
}

const formatSessionListWeekday = (date: Date, intl: IntlShape): string => {
  if (isEnglishLocale(intl)) {
    return format(date, 'EEE');
  }
  return formatLocalizedSessionListDate(date, intl, { weekday: 'short' });
};

const formatSessionListYear = (date: Date, intl: IntlShape): string => {
  if (isEnglishLocale(intl)) {
    return format(date, 'yyyy');
  }
  return formatLocalizedSessionListDate(date, intl, { year: 'numeric' });
};

/**
 * Formats the date group for the stacked sub-label. Returns null when the
 * variant voids the whole group (`withoutDate`).
 */
export function formatStackedSessionDateGroup(
  date: Date,
  intl: IntlShape,
  variant: StackedDateVariant
): string | null {
  if (variant === 'withoutDate') return null;
  if (variant === 'now') {
    return intl.formatMessage({ defaultMessage: 'Now', id: 'tgvES0v2HS' });
  }
  const weekday = formatSessionListWeekday(date, intl);
  const dayMonth = formatSessionListDateShort(date, intl);
  if (variant === 'withoutYear') return `${weekday}, ${dayMonth}`;
  return `${weekday}, ${dayMonth}, ${formatSessionListYear(date, intl)}`;
}

/**
 * Time-of-day counterpart of {@link formatSessionListDate} for the Time column.
 * English keeps the existing `date-fns` "3:06pm" output; Arabic and all other
 * app locales route through `intl.formatTime` so French/German/etc. get 24-hour
 * (or locale-native) labels instead of English am/pm.
 */
export function formatSessionListTime(date: Date, intl: IntlShape): string {
  if (isArabicLocale(intl) || !isEnglishLocale(intl)) {
    return intl.formatTime(date, { hour: 'numeric', minute: '2-digit' });
  }
  return format(date, 'h:mmaaa');
}

/** Figma's expanded Start Now time treatment separates time and meridiem. */
export function formatExpandedSessionListTime(date: Date, intl: IntlShape): string {
  if (!isEnglishLocale(intl)) {
    return formatSessionListTime(date, intl);
  }
  return format(date, 'h:mm aaa');
}

export function getSessionDateGroupLabel(
  group: SessionDateGroup,
  intl: IntlShape
): string {
  switch (group) {
    case 'upcoming':
      return intl.formatMessage({ defaultMessage: 'Upcoming', id: '6/aEFZbs2i' });
    case 'today':
      return intl.formatMessage({ defaultMessage: 'Today', id: 'zWgbGgjUUg' });
    case 'yesterday':
      return intl.formatMessage({ defaultMessage: 'Yesterday', id: '6dIxDP1C8c' });
    case 'older':
      return intl.formatMessage({ defaultMessage: 'Older', id: 'iIeVwABUWb' });
  }
}
